import { useMemo, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar.jsx";
import { Button } from "../../components/ui/button.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet.jsx";
import { UserPlus } from "lucide-react";

const toSearchValue = (value) => String(value || "").toLowerCase();

export default function VoiceParticipantsSheet({
  open,
  onOpenChange,
  participants = [],
  directory = [],
  onInvite,
}) {
  const [search, setSearch] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");

  const filteredDirectory = useMemo(() => {
    if (!search.trim()) return directory;
    const needle = toSearchValue(search);
    return directory.filter((entry) => {
      return toSearchValue(entry.name).includes(needle) || toSearchValue(entry.email).includes(needle);
    });
  }, [directory, search]);

  const handleManualInvite = () => {
    const email = inviteEmail.trim();
    if (!email) return;
    const name = inviteName.trim() || email.split("@")[0];
    onInvite?.({ id: email, name, email, role: "Invite sent" });
    setInviteName("");
    setInviteEmail("");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[360px] sm:w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left text-lg font-semibold">Participants</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-6 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current attendees</div>
            {participants.length ? (
              <ul className="mt-3 space-y-2">
                {participants.map((person) => {
                  const key = person.id || person.email || person.name;
                  return (
                    <li key={key} className="flex items-center justify-between rounded-2xl border border-border bg-muted/40 px-3 py-2">
                      <span className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={`https://i.pravatar.cc/80?u=${encodeURIComponent(person.email || person.name)}`} alt={person.name} referrerPolicy="no-referrer" />
                          <AvatarFallback>{person.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span>
                          <span className="block font-semibold text-foreground">{person.name}</span>
                          {person.email && <span className="text-xs text-muted-foreground">{person.email}</span>}
                        </span>
                      </span>
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{person.role || "Participant"}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="mt-3 text-xs text-muted-foreground">No one has joined yet.</div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <UserPlus className="h-3.5 w-3.5" /> Invite from directory
            </div>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search teammates"
              className="mt-3"
            />
            <div className="mt-3 max-h-48 overflow-auto space-y-2">
              {filteredDirectory.length ? (
                filteredDirectory.map((entry) => (
                  <button
                    key={entry.id || entry.email || entry.name}
                    type="button"
                    onClick={() => onInvite?.(entry)}
                    className="flex w-full items-center justify-between rounded-xl border border-transparent bg-background/60 px-3 py-2 text-left text-xs transition hover:border-primary/40 hover:bg-primary/5"
                  >
                    <span className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={`https://i.pravatar.cc/80?u=${encodeURIComponent(entry.email || entry.name)}`} alt={entry.name} referrerPolicy="no-referrer" />
                        <AvatarFallback>{entry.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span>
                        <span className="block text-sm font-semibold text-foreground">{entry.name}</span>
                        {entry.email && <span className="text-[11px] text-muted-foreground">{entry.email}</span>}
                      </span>
                    </span>
                    <span className="text-[11px] uppercase tracking-wide text-primary">Invite</span>
                  </button>
                ))
              ) : (
                <div className="text-xs text-muted-foreground">No matches found.</div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-muted/30 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Invite via email</div>
            <div className="mt-3 space-y-2">
              <Input
                value={inviteName}
                onChange={(event) => setInviteName(event.target.value)}
                placeholder="Name (optional)"
              />
              <Input
                type="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="name@example.com"
              />
              <Button type="button" size="sm" onClick={handleManualInvite} disabled={!inviteEmail.trim()} className="w-full">
                Send invite
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
